import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { authClient } from "../api/authClient";
import { getRepresentativeProfile } from "../../registered/corporate_details/representative/representativeInteractionService";

const AuthContext = createContext(null);

const mapResultToUser = (result) => {
  const data = result?.raw;
  if (!data || (!data.user && !data.session)) return null;
  return {
    siteUserGuid: data.user?.siteUserGuid || data.session?.siteUserGuid || null,
    email: data.user?.emailAddress || null,
    userName: data.user?.userName || data.user?.emailAddress || null,
    fullName: null,
    sessionId: data.session?.sessionId || null,
  };
};

const siteKey = process.env.REACT_APP_SITE_KEY;

// Full name only exists once a user has gone through the Register Company
// identity-capture step (Corp.Z_03_RepresentativeProfile) - sign-up itself
// never collects a display name. Falls back silently (email is still shown).
const loadFullName = async () => {
  const profile = await getRepresentativeProfile({ siteKey });
  return profile?.FullName || profile?.fullName || null;
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [matrix, setMatrix] = useState([]); 
  const [isInitialising, setIsInitialising] = useState(true);

  const initAuth = useCallback(async () => {
    try {
      // 1. AUTH INITIALIZES FIRST (Identity Handshake)
      const result = await authClient.getSession();
      
      if (result?.ok) {
        setUser(mapResultToUser(result));

        // 2. SECURITY LAYER ACTIVATES (Derive Matrix from Identity)
        const storedMatrix = authClient.getMatrix();
        if (storedMatrix) setMatrix(storedMatrix);

        // 3. Best-effort name lookup - doesn't block boot, doesn't fail login
        loadFullName().then((fullName) => {
          if (fullName) setUser((prev) => (prev ? { ...prev, fullName } : prev));
        });
      }
    } catch (err) {
      console.error("Architectural Boot Failure:", err);
    } finally {
      // 3. UI RENDERS ONLY AFTER BOTH ARE RESOLVED
      setIsInitialising(false);
    }
  }, []);

  useEffect(() => {
    initAuth();
  }, [initAuth]);

  const login = useCallback(async (credentials) => {
    const result = await authClient.login(credentials);
    if (result.ok) {
      // Atomic update: Identity, then Security
      setUser(mapResultToUser(result));
      setMatrix(result.matrix || []);

      loadFullName().then((fullName) => {
        if (fullName) setUser((prev) => (prev ? { ...prev, fullName } : prev));
      });
    }
    return result;
  }, []);

  // Google / Amazon (@nrgpix/auth-social -> @nrgpix/api-social): same session shape as a password sign-in.
  const socialLogin = useCallback(async ({ provider, token }) => {
    const result = await authClient.socialSignIn({ provider, token });
    if (result.ok) {
      setUser(mapResultToUser(result));
      setMatrix(result.matrix || []);
      loadFullName().then((fullName) => {
        if (fullName) setUser((prev) => (prev ? { ...prev, fullName } : prev));
      });
    }
    return result;
  }, []);

  const logout = useCallback(async () => {
    await authClient.signOut();
    setUser(null);
    setMatrix([]);
  }, []);

  const value = useMemo(() => ({
    user,
    matrix,
    isInitialising,
    isAuthenticated: !!user,
    login,
    socialLogin,
    logout
  }), [user, matrix, isInitialising, login, socialLogin, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

AuthProvider.propTypes = { children: PropTypes.node.isRequired };
export const useAuth = () => useContext(AuthContext);